import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {FormField} from 'shells/form/form-field';
import {Modal} from 'shells/modal/modal';
import {TextField} from 'shells/textfield/textfield';
import {MARKETING_AUTOMATIONS_TOUR_ENTER_URL_ID} from 'shells/tours/tours';

import {appHistory} from '../../../history';

import {useCreateMoneyPage} from './graphql/hooks/use-create-money-page';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    progressTour: () => void;
    pauseTour: () => void;
    exitTour: () => void;
};

export function CreateMoneyPageModal(props: Props) {
    const [url, setUrl] = React.useState<string>('');
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(undefined);
    const [pageName, setPageName] = React.useState<string>('');

    const {createMoneyPage, isLoading, error} = useCreateMoneyPage();

    // Progress tour when modal opens and element is ready
    React.useEffect(() => {
        if (props.isOpen && document.getElementById(MARKETING_AUTOMATIONS_TOUR_ENTER_URL_ID)) {
            props.progressTour();
        }
    }, [props, props.isOpen, props.progressTour]);

    const handleSave = () => {
        createMoneyPage({
            name: pageName,
            url: url,
        })
            .then((result) => {
                if (
                    result &&
                    result.data &&
                    result.data.maUrlCreate &&
                    result.data.maUrlCreate.url
                ) {
                    const {htmlUrlPath} = result.data.maUrlCreate.url;
                    // Pause the tour before navigation to avoid anchoring issues
                    props.pauseTour();
                    appHistory.push(htmlUrlPath);
                }
                props.onClose();
            })
            .catch((e) => {
                const message = safelyGetGraphQLErrorMessage(e);
                setErrorMessage(message);
            });
    };

    const handleClose = () => {
        setUrl('');
        setPageName('');
        setErrorMessage(undefined);
        props.exitTour();
        props.onClose();
    };

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={handleClose}
            headerText='Add an automation'
            isCentered={true}
            canClickOutsideToClose={true}
            fitHeight={true}
            maxWidth={400}
        >
            <FormField
                label='Automation name'
                innerId='money-page-name'
                tooltipInfo='This is just for you and your own organization.'
            >
                <TextField
                    id='money-page-name'
                    value={pageName}
                    onChange={setPageName}
                    placeholder='Enter automation name'
                    focusOnMount={true}
                    onEnter={pageName && url ? handleSave : undefined}
                />
            </FormField>
            <div id={MARKETING_AUTOMATIONS_TOUR_ENTER_URL_ID}>
                <FormField
                    label='On which page should this automation run?'
                    innerId='money-page-url'
                    errorMessage={errorMessage}
                >
                    <TextField
                        id='money-page-url'
                        value={url}
                        onChange={(value) => {
                            setUrl(value);
                            setErrorMessage(undefined);
                        }}
                        placeholder='www.example.com'
                        onEnter={url && pageName ? handleSave : undefined}
                        hasError={Boolean(errorMessage)}
                    />
                </FormField>
            </div>
            <div className='flex justify-sb pb-32'>
                <Button variant='text-secondary' size='big' onClick={handleClose}>
                    Cancel
                </Button>
                <SaveButton
                    size='big'
                    isSaving={isLoading}
                    // @ts-expect-error
                    onClick={handleSave}
                    disabled={!url || !pageName}
                    buttonText='Add'
                    savingText='Adding…'
                    errorSaving={Boolean(error)}
                />
            </div>
        </Modal>
    );
}
